import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import fp from "lodash/fp";
import { Col, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";

import {
	AddressSearchInputHookForm,
	HookFormInputGroup,
	HookFormRadioButton,
	HookSelectFormGroup,
} from "components";
import {
	AddressCoordinatesConstants,
	AddressSectionFields,
	GoogleAddressComponentsFields,
	GoogleAddressOptionFields,
} from "globalConstants";
import { checkAddressFieldsEmpty, checkIsLatOrLngManual } from "helpers";
import { TypeOfLocationValues, CountriesList } from "types";
import {
	alphaNumericNormalizer,
	matchesAddressRegex,
	matchesCityRegex,
	maxLength2,
	maxLength30,
	maxLength50,
	maxLength9,
	minLength2,
	minLength5,
	normalizeAll,
	onlyAlphanumericAndSpecialCharacters,
	onlyTwoUpperCaseLetters,
	required,
	trimMidSentenceNormalizer,
	trimNormalizer,
	validateLatLng,
	validateWhenTypeOfLocationIsOther,
} from "validators";

import AddressTextBlock from "./addressTextBlock";

import styles from "./addressSection.module.scss";

type AddressSectionProps = {
	sectionName?: string;
	isLoading?: boolean;
	isRequired?: boolean;
	isTypeOfLocationRequired?: boolean;
	hasTypeOfLocation?: boolean;
	isAddressTheSameName?: string;
	isAddressTheSameLabel?: string;
};

const AddressSectionHookForm = ({
	sectionName,
	isLoading = false,
	isRequired = false,
	isTypeOfLocationRequired = false,
	hasTypeOfLocation = false,
	isAddressTheSameName = "",
	isAddressTheSameLabel = "",
}: AddressSectionProps) => {
	const [isManualMode, setIsManualMode] = useState<boolean>(false);

	const getFieldName = useCallback(
		name => `${sectionName}.${name}`,
		[sectionName],
	);

	const {
		register,
		watch,
		setValue,
		formState: { errors },
	} = useFormContext();

	const formValues = sectionName ? watch(sectionName) : watch();

	const countryCode = watch(
		getFieldName(AddressSectionFields.CountryCode.name),
	);

	const [lat, lng] = watch([
		getFieldName(GoogleAddressOptionFields.Lat),
		getFieldName(GoogleAddressOptionFields.Lng),
	]);

	const isAddressTheSame = watch(isAddressTheSameName) === "true";

	const handleSelect = useCallback(
		addressDetails => {
			setIsManualMode(false);

			if (fp.isEmpty(addressDetails)) {
				setValue(getFieldName(AddressSectionFields.Street.name), "");
				setValue(
					getFieldName(AddressSectionFields.Apartments.name),
					"",
				);
				setValue(getFieldName(AddressSectionFields.City.name), "");
				setValue(getFieldName(AddressSectionFields.SubCity.name), "");
				setValue(getFieldName(AddressSectionFields.Region.name), "");
				setValue(
					getFieldName(AddressSectionFields.StreetNumber.name),
					"",
				);
				setValue(
					getFieldName(AddressSectionFields.PostalCode.name),
					"",
				);
				setValue(getFieldName(AddressSectionFields.Country.name), "");
				setValue(
					getFieldName(AddressSectionFields.CountryCode.name),
					"",
				);
				setValue(
					getFieldName(AddressSectionFields.TypeOfLocation.name),
					"",
				);
				setValue(getFieldName(GoogleAddressOptionFields.Lat), "");
				setValue(getFieldName(GoogleAddressOptionFields.Lng), "");
				setValue(getFieldName(GoogleAddressOptionFields.Label), "");

				return;
			}

			const addressComponents =
				addressDetails?.[GoogleAddressOptionFields.Address];

			const newAddressDetails = {
				...addressDetails,
				...addressComponents,
				[GoogleAddressComponentsFields.Street.name]: `${
					addressComponents?.[
						GoogleAddressComponentsFields.StreetNumber.name
					] ?? ""
				} ${
					addressComponents?.[
						GoogleAddressComponentsFields.Street.name
					] ?? ""
				}`,
			};

			delete newAddressDetails[GoogleAddressOptionFields.Address];

			setValue(sectionName || "", newAddressDetails);
		},
		[getFieldName, sectionName, setValue],
	);

	const handleCountryChange = useCallback(
		option => {
			setValue(
				getFieldName(AddressSectionFields.CountryCode.name),
				option.value,
			);
		},
		[getFieldName, setValue],
	);

	const manualModeController = useCallback(
		isManual => {
			setIsManualMode(isManual);

			if (isManual) {
				setValue(
					getFieldName(GoogleAddressOptionFields.Lat),
					AddressCoordinatesConstants.ManualAddress.lat,
				);
				setValue(
					getFieldName(GoogleAddressOptionFields.Lng),
					AddressCoordinatesConstants.ManualAddress.lng,
				);
			}
		},
		[getFieldName, setValue],
	);

	const addRequiredValidation = useCallback(
		(validators: { [key: string]: any } = {}) =>
			isRequired ? { required, ...validators } : validators,
		[isRequired],
	);

	const validationSearchField = useMemo(() => {
		return isManualMode
			? undefined
			: addRequiredValidation({
					validateLatLng: validateLatLng(sectionName),
			  });
	}, [addRequiredValidation, isManualMode, sectionName]);

	const FieldComponent = useCallback(
		({
			fieldData: { name, displayName },
			isDisabled,
			isLoading,
			component: Component,
			validator,
			...props
		}) => {
			if (isManualMode) {
				if (Component) {
					return (
						<Component
							{...props}
							name={getFieldName(name)}
							label={displayName}
							isDisabled={isAddressTheSame || isDisabled}
							showRequiredMark={isRequired}
							blurLoading={isLoading}
						/>
					);
				} else {
					return (
						<HookFormInputGroup
							{...props}
							register={register}
							name={getFieldName(name)}
							disabled={isAddressTheSame || isDisabled}
							validator={addRequiredValidation(validator)}
							showRequiredMark={isRequired}
							label={displayName}
							colInput={6}
							colLabel={6}
							blurLoading={isLoading}
							autoComplete="off"
							errors={errors}
						/>
					);
				}
			} else {
				return (
					<AddressTextBlock
						value={formValues?.[name]}
						label={`${displayName}: `}
						isDisabled={isAddressTheSame || isDisabled}
					/>
				);
			}
		},
		[
			addRequiredValidation,
			errors,
			formValues,
			getFieldName,
			isAddressTheSame,
			isManualMode,
			isRequired,
			register,
		],
	);

	useEffect(() => {
		// Set initial appearance of the component
		if (
			checkIsLatOrLngManual({
				lat,
				lng,
			}) &&
			!checkAddressFieldsEmpty(formValues)
		) {
			setIsManualMode(true);
		} else {
			setIsManualMode(false);
		}
	}, [
		countryCode,
		formValues,
		getFieldName,
		lat,
		lng,
		sectionName,
		setValue,
	]);

	useEffect(() => {
		setValue(
			getFieldName(AddressSectionFields.Country.name),
			CountriesList.getByValue(countryCode),
		);
	}, [countryCode, getFieldName, setValue]);

	return (
		<>
			{isAddressTheSameName && (
				<Row className="mb-3">
					<Col md={12}>
						<div className="mb-3">{isAddressTheSameLabel}</div>
						<div className={styles.wrappRadio}>
							<HookFormRadioButton
								id={isAddressTheSameName}
								name={isAddressTheSameName}
								register={register}
								value="true"
								label="Yes"
								blurLoading={isLoading}
							/>
							<HookFormRadioButton
								id={`not-${isAddressTheSameName}`}
								name={isAddressTheSameName}
								register={register}
								value="false"
								label="No"
								blurLoading={isLoading}
							/>
						</div>
					</Col>
				</Row>
			)}
			{!isAddressTheSame && (
				<>
					<Row>
						<div id="myDiv"></div>
						<Col>
							<AddressSearchInputHookForm
								label={
									AddressSectionFields.SearchAddress
										.displayName
								}
								validate={validationSearchField}
								name={getFieldName(
									AddressSectionFields.SearchAddress.name,
								)}
								showRequiredMark={!isManualMode && isRequired}
								smLabel={3}
								smInput={9}
								onSelect={handleSelect}
								isLoading={isLoading}
								isDisabled={isAddressTheSame}
								labelClassName={styles.addressSearchLabel}
								inputClassName={styles.addressSearchInput}
								manualModeController={manualModeController}
							/>
						</Col>
					</Row>

					<Row className="mb-3">
						<Col lg={5}>
							<FieldComponent
								fieldData={AddressSectionFields.Street}
								validator={{
									matchesAddressRegex,
									minLength2,
									maxLength50,
								}}
								normalize={normalizeAll([
									onlyAlphanumericAndSpecialCharacters,
									trimMidSentenceNormalizer,
								])}
							/>
							<FieldComponent
								fieldData={AddressSectionFields.City}
								validator={{
									minLength2,
									maxLength30,
									matchesCityRegex,
								}}
								normalize={normalizeAll([
									onlyAlphanumericAndSpecialCharacters,
									trimMidSentenceNormalizer,
								])}
							/>
							<FieldComponent
								fieldData={AddressSectionFields.Region}
								validator={{ minLength2, maxLength2 }}
								normalize={onlyTwoUpperCaseLetters}
							/>
							<FieldComponent
								fieldData={AddressSectionFields.PostalCode}
								validator={{ minLength5, maxLength9 }}
								normalize={alphaNumericNormalizer}
							/>
							<FieldComponent
								fieldData={AddressSectionFields.Country}
								component={HookSelectFormGroup}
								options={CountriesList.getOptions()}
								getOptionLabel={option => option.displayName}
								getOptionValue={option => option.value}
								rules={{
									validate: isRequired
										? {
												required,
										  }
										: undefined,
								}}
								onChange={handleCountryChange}
							/>
						</Col>
						<Col lg={{ span: 5, offset: 2 }}>
							<HookFormInputGroup
								name={getFieldName(
									AddressSectionFields.Apartments.name,
								)}
								register={register}
								label={
									AddressSectionFields.Apartments.displayName
								}
								colInput={6}
								colLabel={6}
								blurLoading={isLoading}
								autoComplete="off"
								normalize={onlyAlphanumericAndSpecialCharacters}
								normalizeOnBlur={trimNormalizer}
								errors={errors}
								disabled={isAddressTheSame}
							/>
							{hasTypeOfLocation && (
								<HookSelectFormGroup
									name={getFieldName(
										AddressSectionFields.TypeOfLocation
											.name,
									)}
									label={
										AddressSectionFields.TypeOfLocation
											.displayName
									}
									options={TypeOfLocationValues.getOptions()}
									getOptionLabel={option =>
										option.displayName
									}
									getOptionValue={option => option.value}
									rules={{
										validate: isTypeOfLocationRequired
											? {
													required,
											  }
											: undefined,
									}}
									showRequiredMark={isTypeOfLocationRequired}
									blurLoading={isLoading}
									isDisabled={isAddressTheSame}
								/>
							)}
							{hasTypeOfLocation &&
								formValues?.[
									AddressSectionFields.TypeOfLocation.name
								]?.name === TypeOfLocationValues.Other.name && (
									<HookFormInputGroup
										register={register}
										name={getFieldName(
											AddressSectionFields
												.OtherTypeOfLocation.name,
										)}
										label={
											AddressSectionFields
												.OtherTypeOfLocation.displayName
										}
										colInput={6}
										colLabel={6}
										blurLoading={isLoading}
										autoComplete="off"
										validator={
											validateWhenTypeOfLocationIsOther
										}
										normalize={normalizeAll([
											onlyAlphanumericAndSpecialCharacters,
											trimMidSentenceNormalizer,
										])}
										normalizeOnBlur={trimNormalizer}
										showRequiredMark
										errors={errors}
										disabled={isAddressTheSame}
									/>
								)}
						</Col>
						<input
							{...register(
								getFieldName(AddressSectionFields.Street.name),
							)}
							hidden
						/>
						<input
							{...register(
								getFieldName(AddressSectionFields.City.name),
							)}
							hidden
						/>
						<input
							{...register(
								getFieldName(AddressSectionFields.Region.name),
							)}
							hidden
						/>
						<input
							{...register(
								getFieldName(
									AddressSectionFields.PostalCode.name,
								),
							)}
							hidden
						/>
						<input
							{...register(
								getFieldName(AddressSectionFields.Country.name),
							)}
							hidden
						/>
						<input
							{...register(
								getFieldName(
									AddressSectionFields.CountryCode.name,
								),
							)}
							hidden
						/>
						<input
							{...register(
								getFieldName(
									AddressSectionFields.TypeOfLocation.name,
								),
							)}
							hidden
						/>
						<input
							{...register(
								getFieldName(GoogleAddressOptionFields.Lat),
							)}
							hidden
						/>
						<input
							{...register(
								getFieldName(GoogleAddressOptionFields.Lng),
							)}
							hidden
						/>
					</Row>
				</>
			)}
		</>
	);
};

export default memo(AddressSectionHookForm);
